import React from "react";
import SidebarAdmin from "../../menu/SidebarAdmin";
import Topbar from "../../menu/Topbar";
import Api from "../../Services/Api";
import "./AdminStoreProducts.css";

const DEFAULT_FORM = {
  categoryId: "",
  title: "",
  description: "",
  price: "",
  points: "",
  promotionalPrice: "",
  showInPromotions: true,
  showInHighlights: true,
};

const currencyFormatter = new Intl.NumberFormat("pt-BR", {
  style: "currency",
  currency: "BRL",
});

const formatCurrency = (value) => {
  const number = Number(value);
  if (!Number.isFinite(number)) return "";
  return currencyFormatter.format(number);
};

const formatFileSize = (size) => {
  const value = Number(size);
  if (!Number.isFinite(value) || value <= 0) return "";
  if (value >= 1024 * 1024) return `${(value / (1024 * 1024)).toFixed(2)} MB`;
  return `${Math.max(1, Math.round(value / 1024))} KB`;
};

const getProductForm = (product) => ({
  categoryId: product?.categoryId ? String(product.categoryId) : "",
  title: product?.title || "",
  description: product?.description || "",
  price: product?.price === null || product?.price === undefined ? "" : String(product.price),
  points: product?.points === null || product?.points === undefined ? "" : String(product.points),
  promotionalPrice:
    product?.promotionalPrice === null || product?.promotionalPrice === undefined
      ? ""
      : String(product.promotionalPrice),
  showInPromotions: Boolean(product?.showInPromotions),
  showInHighlights: Boolean(product?.showInHighlights),
});

const getProductImageItems = (product) =>
  (Array.isArray(product?.images) ? product.images : []).map((image, index) => ({
    kind: "existing",
    id: image.id,
    url: image.url,
    name: image.originalName || `Foto ${index + 1}`,
    size: image.size,
  }));

const createNewImageItem = (file) => ({
  kind: "new",
  key: `${Date.now()}-${file.name}-${file.lastModified}-${Math.random().toString(16).slice(2)}`,
  file,
  previewUrl: URL.createObjectURL(file),
  name: file.name,
  size: file.size,
});

const revokeNewImagePreviews = (items = []) => {
  items.forEach((item) => {
    if (item.kind === "new" && item.previewUrl) {
      URL.revokeObjectURL(item.previewUrl);
    }
  });
};

const AdminStoreProducts = () => {
  const [menuClose, setMenuClose] = React.useState(false);
  const [categories, setCategories] = React.useState([]);
  const [products, setProducts] = React.useState([]);
  const [imageItems, setImageItems] = React.useState([]);
  const [editingProductId, setEditingProductId] = React.useState(null);
  const [form, setForm] = React.useState(DEFAULT_FORM);
  const [loading, setLoading] = React.useState(true);
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState("");
  const [success, setSuccess] = React.useState("");
  const fileInputRef = React.useRef(null);
  const imageItemsRef = React.useRef([]);

  const toggleMenu = () => setMenuClose((prev) => !prev);
  const isEditing = Boolean(editingProductId);

  React.useEffect(() => {
    imageItemsRef.current = imageItems;
  }, [imageItems]);

  React.useEffect(
    () => () => {
      revokeNewImagePreviews(imageItemsRef.current);
    },
    []
  );

  const loadData = React.useCallback(async () => {
    try {
      setLoading(true);
      setError("");

      const [categoriesResponse, productsResponse] = await Promise.all([
        Api.get("/admin/store-categories"),
        Api.get("/admin/store-products"),
      ]);

      setCategories(Array.isArray(categoriesResponse?.data?.items) ? categoriesResponse.data.items : []);
      setProducts(Array.isArray(productsResponse?.data?.items) ? productsResponse.data.items : []);
    } catch (err) {
      console.error("Erro ao carregar dados da store:", err);
      setError("Nao foi possivel carregar categorias e produtos da Store.");
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    loadData();
  }, [loadData]);

  const clearFileInput = React.useCallback(() => {
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }, []);

  const resetForm = React.useCallback(() => {
    revokeNewImagePreviews(imageItemsRef.current);
    setForm(DEFAULT_FORM);
    setImageItems([]);
    setEditingProductId(null);
    clearFileInput();
  }, [clearFileInput]);

  const handleChange = (field) => (event) => {
    const value =
      event.target.type === "checkbox" ? event.target.checked : event.target.value;

    setForm((current) => ({
      ...current,
      [field]: value,
    }));

    setError("");
    setSuccess("");
  };

  const handleSelectFiles = (event) => {
    const nextFiles = Array.from(event.target.files || []);
    if (nextFiles.length === 0) return;

    setImageItems((current) => [...current, ...nextFiles.map(createNewImageItem)]);
    setError("");
    setSuccess("");
    clearFileInput();
  };

  const moveImage = (index, direction) => {
    setImageItems((current) => {
      const nextIndex = index + direction;
      if (nextIndex < 0 || nextIndex >= current.length) return current;

      const next = [...current];
      [next[index], next[nextIndex]] = [next[nextIndex], next[index]];
      return next;
    });
  };

  const removeImage = (index) => {
    setImageItems((current) => {
      const item = current[index];
      if (item) revokeNewImagePreviews([item]);
      return current.filter((_image, currentIndex) => currentIndex !== index);
    });
    setError("");
    setSuccess("");
  };

  const startEdit = (product) => {
    resetForm();
    setEditingProductId(product.id);
    setForm(getProductForm(product));
    setImageItems(getProductImageItems(product));
    setError("");
    setSuccess("");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const buildFormData = () => {
    const formData = new FormData();
    const newImageKeys = [];

    formData.append("categoryId", form.categoryId);
    formData.append("title", form.title);
    formData.append("description", form.description);
    formData.append("price", form.price);
    formData.append("points", form.points);
    formData.append("promotionalPrice", form.promotionalPrice);
    formData.append("showInPromotions", String(form.showInPromotions));
    formData.append("showInHighlights", String(form.showInHighlights));

    imageItems
      .filter((item) => item.kind === "new")
      .forEach((item) => {
        formData.append("images", item.file);
        newImageKeys.push(item.key);
      });

    if (isEditing) {
      formData.append(
        "imageOrder",
        JSON.stringify(
          imageItems.map((item) =>
            item.kind === "existing" ? `existing:${item.id}` : `new:${item.key}`
          )
        )
      );
      formData.append("newImageKeys", JSON.stringify(newImageKeys));
    }

    return formData;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (imageItems.length === 0) {
      setError("Envie ao menos uma foto do produto.");
      return;
    }

    try {
      setSubmitting(true);
      setError("");
      setSuccess("");

      const formData = buildFormData();
      const response = isEditing
        ? await Api.put(`/admin/store-products/${editingProductId}`, formData, {
            timeout: 30000,
          })
        : await Api.post("/admin/store-products", formData, {
            timeout: 30000,
          });

      const saved = response?.data?.item;
      if (saved) {
        setProducts((current) =>
          isEditing
            ? current.map((item) => (item.id === saved.id ? saved : item))
            : [saved, ...current]
        );
      }

      resetForm();
      setSuccess(isEditing ? "Produto atualizado com sucesso." : "Produto criado com sucesso.");
    } catch (err) {
      console.error("Erro ao salvar produto da store:", err);
      setError(
        err?.code === "ECONNABORTED"
          ? "O envio demorou mais do que o esperado. Tente novamente."
          : err?.response?.data?.message || "Nao foi possivel salvar o produto."
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (productId) => {
    if (!window.confirm("Deseja remover este produto da Store?")) return;

    try {
      setSubmitting(true);
      setError("");
      setSuccess("");
      await Api.delete(`/admin/store-products/${productId}`);
      setProducts((current) => current.filter((item) => item.id !== productId));
      if (editingProductId === productId) {
        resetForm();
      }
      setSuccess("Produto removido com sucesso.");
    } catch (err) {
      console.error("Erro ao remover produto da store:", err);
      setError(err?.response?.data?.message || "Nao foi possivel remover o produto.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="app-container">
      <SidebarAdmin close={menuClose} storeProductsActive="active" />

      <main className="main-content">
        <Topbar page="Store / Produtos" onToggleMenu={toggleMenu} />

        <section className="admin-store-products-grid">
          <article className="admin-store-products-panel">
            <div className="admin-store-products-header">
              <div>
                <h2>{isEditing ? "Editar produto" : "Cadastrar produto"}</h2>
                <p>
                  Adicione titulo, descricao, valor, pontos e varias fotos. Use os botoes de
                  ordem para definir como o carrossel aparece na Store.
                </p>
              </div>
              <span className="admin-store-products-badge">Super administrador</span>
            </div>

            <form className="admin-store-products-form" onSubmit={handleSubmit}>
              <label className="admin-store-products-field">
                <span>Categoria</span>
                <select value={form.categoryId} onChange={handleChange("categoryId")}>
                  <option value="">Selecione</option>
                  {categories.map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </select>
              </label>

              <label className="admin-store-products-field admin-store-products-field-wide">
                <span>Titulo</span>
                <input
                  value={form.title}
                  onChange={handleChange("title")}
                  placeholder="Ex.: Camisa Polo Premium"
                />
              </label>

              <label className="admin-store-products-field admin-store-products-field-full">
                <span>Descricao</span>
                <textarea
                  value={form.description}
                  onChange={handleChange("description")}
                  placeholder="Descreva o produto"
                  rows={4}
                />
              </label>

              <label className="admin-store-products-field">
                <span>Valor</span>
                <input value={form.price} onChange={handleChange("price")} placeholder="79.90" />
              </label>

              <label className="admin-store-products-field">
                <span>Pontos</span>
                <input value={form.points} onChange={handleChange("points")} placeholder="1200" />
              </label>

              <label className="admin-store-products-field">
                <span>Valor promocional</span>
                <input
                  value={form.promotionalPrice}
                  onChange={handleChange("promotionalPrice")}
                  placeholder="59.90"
                />
              </label>

              <div className="admin-store-products-checkboxes admin-store-products-field-full">
                <label className="admin-store-products-checkbox">
                  <input
                    type="checkbox"
                    checked={form.showInPromotions}
                    onChange={handleChange("showInPromotions")}
                  />
                  <span>Mostrar em promocoes</span>
                </label>

                <label className="admin-store-products-checkbox">
                  <input
                    type="checkbox"
                    checked={form.showInHighlights}
                    onChange={handleChange("showInHighlights")}
                  />
                  <span>Mostrar em destaque</span>
                </label>
              </div>

              <label
                className="admin-store-products-upload-box admin-store-products-field-full"
                htmlFor="store-product-images"
              >
                <span>Selecionar fotos do produto</span>
                <small>
                  Envie uma ou mais imagens e ajuste a ordem antes de salvar o produto.
                </small>
              </label>

              <input
                ref={fileInputRef}
                id="store-product-images"
                type="file"
                accept="image/*"
                multiple
                onChange={handleSelectFiles}
              />

              {imageItems.length > 0 ? (
                <div className="admin-store-products-file-list admin-store-products-field-full">
                  {imageItems.map((image, index) => (
                    <div
                      className="admin-store-products-file-item"
                      key={image.kind === "existing" ? `existing-${image.id}` : image.key}
                    >
                      <div className="admin-store-products-file-thumb">
                        <img
                          src={image.kind === "existing" ? image.url : image.previewUrl}
                          alt={image.name}
                        />
                      </div>

                      <div className="admin-store-products-file-info">
                        <strong>{image.name}</strong>
                        <span>
                          Foto {index + 1}
                          {formatFileSize(image.size) ? ` - ${formatFileSize(image.size)}` : ""}
                          {image.kind === "new" ? " - nova" : ""}
                        </span>
                      </div>

                      <div className="admin-store-products-file-actions">
                        <button
                          type="button"
                          onClick={() => moveImage(index, -1)}
                          disabled={index === 0 || submitting}
                        >
                          Subir
                        </button>
                        <button
                          type="button"
                          onClick={() => moveImage(index, 1)}
                          disabled={index === imageItems.length - 1 || submitting}
                        >
                          Descer
                        </button>
                        <button
                          type="button"
                          className="danger"
                          onClick={() => removeImage(index)}
                          disabled={submitting}
                        >
                          Remover
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : null}

              <div className="admin-store-products-actions admin-store-products-field-full">
                <button type="submit" disabled={submitting}>
                  {submitting
                    ? "Salvando..."
                    : isEditing
                      ? "Salvar alteracoes"
                      : "Criar produto"}
                </button>
                {isEditing ? (
                  <button type="button" className="secondary" onClick={resetForm} disabled={submitting}>
                    Cancelar edicao
                  </button>
                ) : null}
              </div>

              {error ? <div className="admin-store-products-message error">{error}</div> : null}
              {success ? <div className="admin-store-products-message success">{success}</div> : null}
            </form>
          </article>

          <article className="admin-store-products-panel">
            <div className="admin-store-products-header compact">
              <div>
                <h2>Produtos cadastrados</h2>
                <p>{loading ? "Carregando..." : `${products.length} produto(s) ativos`}</p>
              </div>
            </div>

            {loading ? (
              <div className="admin-store-products-empty">Carregando produtos...</div>
            ) : products.length === 0 ? (
              <div className="admin-store-products-empty">Nenhum produto cadastrado ainda.</div>
            ) : (
              <div className="admin-store-products-list">
                {products.map((product) => (
                  <article className="admin-store-product-card" key={product.id}>
                    <div className="admin-store-product-preview">
                      {product.images?.[0]?.url ? (
                        <img src={product.images[0].url} alt={product.title} />
                      ) : (
                        <div className="admin-store-product-placeholder">Sem imagem</div>
                      )}
                    </div>

                    <div className="admin-store-product-body">
                      <strong>{product.title}</strong>
                      <span>{product.categoryName}</span>
                      {product.description ? <p>{product.description}</p> : null}

                      <div className="admin-store-product-meta">
                        {product.price !== null ? <span>Valor: {formatCurrency(product.price)}</span> : null}
                        {product.promotionalPrice !== null ? (
                          <span>Promocao: {formatCurrency(product.promotionalPrice)}</span>
                        ) : null}
                        {product.points !== null ? <span>Pontos: {product.points}</span> : null}
                        {product.showInPromotions ? <span>Em promocoes</span> : null}
                        {product.showInHighlights ? <span>Em destaque</span> : null}
                        <span>{product.images?.length || 0} foto(s)</span>
                      </div>
                    </div>

                    <div className="admin-store-product-actions">
                      <button
                        type="button"
                        className="admin-store-product-edit"
                        onClick={() => startEdit(product)}
                        disabled={submitting}
                      >
                        Editar
                      </button>
                      <button
                        type="button"
                        className="admin-store-product-remove"
                        onClick={() => handleDelete(product.id)}
                        disabled={submitting}
                      >
                        Remover
                      </button>
                    </div>
                  </article>
                ))}
              </div>
            )}
          </article>
        </section>
      </main>
    </div>
  );
};

export default AdminStoreProducts;
